import type { ClientKind } from "@hamlak/shared";
import { t, type MessageKey } from "./ko";

/**
 * 서버가 내려주는 데이터 코드 → 사용자 문구. 서버·DB 는 코드만 알고 문구 키 체계는 클라가 소유한다(두 이름은 결합하지 않는다).
 * 모르는 코드는 null — 호출부가 숨기거나 폴백을 고른다. 운영이 코드를 먼저 추가해도 화면이 깨지지 않게.
 */
const lookup = (keys: Record<string, MessageKey>, code: string): string | null => {
  const key = Object.prototype.hasOwnProperty.call(keys, code) ? keys[code] : undefined;
  return key ? t(key) : null;
};

const REPORT_REASON_KEYS: Record<string, MessageKey> = {
  sexual: "report.reason.sexual",
  violence: "report.reason.violence",
  minor: "report.reason.minor",
  privacy: "report.reason.privacy",
  off_character: "report.reason.off_character",
  other: "report.reason.other",
};
export const reportReasonText = (code: string) => lookup(REPORT_REASON_KEYS, code);

const DELETE_REASON_KEYS: Record<string, MessageKey> = {
  character: "delete.reason.character",
  chat_quality: "delete.reason.chat_quality",
  expensive: "delete.reason.expensive",
  other_service: "delete.reason.other_service",
  not_used: "delete.reason.not_used",
  other: "delete.reason.other",
};
export const deleteReasonText = (code: string) => lookup(DELETE_REASON_KEYS, code);

/** RadioGroup 옵션: 문구를 모르는 코드는 뺀다 */
export const reasonOptions = (codes: readonly string[], text: (code: string) => string | null) =>
  codes.flatMap((code) => {
    const label = text(code);
    return label ? [{ value: code, label }] : [];
  });

/**
 * 결제 스토어 코드(서버 ProductPlatform·purchases.store 와 같은 google|apple|web). 스토어 이름이 들어가는 문구는 키 끝에 이 코드를 붙여 둔다(ios.md i5).
 * 이 기기의 결제 채널은 clientKind 로 고른다: ios → apple, web → web(토스페이먼츠), 그 외 → google.
 * 내역 행의 스토어는 기기가 아니라 거래 기준(Transaction.meta.store)이다.
 */
export type StoreCode = "google" | "apple" | "web";
/** ios → apple, web → web(토스페이먼츠, 결정 10), 그 외 google */
export const storeOf = (clientKind: ClientKind): StoreCode => (clientKind === "ios" ? "apple" : clientKind === "web" ? "web" : "google");
type StoreMessage = "history.status.paid" | "history.status.refunded" | "support.guide.orderId" | "store.done.methodValue" | "notice.refundRevoked.store";
export const storeKey = (base: StoreMessage, store: StoreCode): MessageKey => `${base}.${store}` satisfies MessageKey;
export const isStoreCode = (v: unknown): v is StoreCode => v === "google" || v === "apple" || v === "web";

// 상품명·배지 문구 조립은 @hamlak/copy (어드민 미리보기와 공유, 어드민 설계 10절 B1). 값의 정본은 packages/copy/src/products.ts
export { bonusLabel, isHighlighted, productName } from "@hamlak/copy";
